import { useSyncExternalStore } from "react";

import type { IncreaseValues } from "./types";

export type DecisionStoreState = {
	increaseValues: IncreaseValues;
	editingRowIds: Set<string>;
};

export type DecisionStore = {
	getState: () => DecisionStoreState;
	setState: (next: Partial<DecisionStoreState>) => void;
	subscribe: (listener: () => void) => () => void;
};

export const createDecisionStore = (initial: DecisionStoreState): DecisionStore => {
	let state = initial;
	const listeners = new Set<() => void>();

	return {
		getState: () => state,
		setState: (next) => {
			state = { ...state, ...next };
			for (const l of listeners) l();
		},
		subscribe: (listener) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
	};
};

export const useIncreaseValue = (
	store: DecisionStore | undefined,
	rowId: string
): IncreaseValues[string] | undefined => {
	const snap = useSyncExternalStore(
		store ? store.subscribe : noopSubscribe,
		store ? () => store.getState().increaseValues : emptySnap
	);
	return snap[rowId];
};

export const useIsEditingRow = (store: DecisionStore | undefined, rowId: string): boolean => {
	const snap = useSyncExternalStore(
		store ? store.subscribe : noopSubscribe,
		store ? () => store.getState().editingRowIds : emptyEditingSnap
	);
	return snap.has(rowId);
};

const noopSubscribe = () => () => {};
const emptyObj: IncreaseValues = {};
const emptySnap = () => emptyObj;
const emptySet = new Set<string>();
const emptyEditingSnap = () => emptySet;
